import { useState, useEffect, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Search } from 'lucide-react';
import {
  getSimilarityPasses, getSimilarityNeighbors, getCornerArchetypes,
} from '../../services/api';
import WeightSliders from './WeightSliders';
import { SERIES_COLORS } from './DriverRadar';
import styles from './SimilarityLabPage.module.css';

const fmtLap = s => {
  if (s == null) return '—';
  if (s < 60) return s.toFixed(3);
  const m = Math.floor(s / 60);
  return `${m}:${(s - m * 60).toFixed(3).padStart(6, '0')}`;
};

const nkey = n => `${n.session_id}|${n.lap_number}|${n.corner_id}`;

function ShapeChart({ title, dataKeyRows, seriesNames, yDomain, yLabel }) {
  return (
    <div className={styles.shapeChart}>
      <div className={styles.shapeTitle}>{title}</div>
      <ResponsiveContainer width="100%" height={190}>
        <LineChart data={dataKeyRows} accessibilityLayer={false}
                   margin={{ top: 4, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="#2c2c2c" strokeDasharray="3 3" />
          <XAxis dataKey="pct" tick={{ fill: '#787878', fontSize: 10 }}
                 tickFormatter={v => `${v}%`} />
          <YAxis tick={{ fill: '#787878', fontSize: 10 }} width={42}
                 domain={yDomain}
                 label={{ value: yLabel, angle: -90, position: 'insideLeft',
                          fill: '#787878', fontSize: 10 }} />
          <Tooltip
            contentStyle={{ background: '#111', border: '1px solid #2c2c2c', fontSize: 12 }}
            labelFormatter={v => `${v}% through window`}
            formatter={(v) => (v == null ? '—' : v.toFixed(3))}
          />
          {seriesNames.map((name, i) => (
            <Line key={name} type="monotone" dataKey={name}
                  stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                  strokeWidth={i === 0 ? 2.5 : 1.6} dot={false}
                  isAnimationActive={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * The debug heart of the lab: pick one of the viewed driver's corner passes,
 * search its nearest neighbors with adjustable block weights, and overlay the
 * 32-point speed/curvature shapes to judge similarity by eye.
 */
export default function CornerExplorer({ trackId, viewUserId, vehicleId, weights, onWeights }) {
  const [passes, setPasses] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [lapNumber, setLapNumber] = useState(null);
  const [cornerId, setCornerId] = useState(null);
  const [scope, setScope] = useState('corner');
  const [otherDrivers, setOtherDrivers] = useState(true);
  const [result, setResult] = useState(null);
  const [checked, setChecked] = useState([]);   // neighbor keys overlaid (max 3)
  const [archetypes, setArchetypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!trackId || vehicleId == null) { setPasses([]); return; }
    let cancelled = false;
    getSimilarityPasses(trackId, viewUserId, vehicleId)
      .then(ps => { if (!cancelled) { setPasses(ps); setSessionId(null); setResult(null); } })
      .catch(e => !cancelled && setError(String(e.message || e)));
    return () => { cancelled = true; };
  }, [trackId, viewUserId, vehicleId]);

  const sessions = useMemo(() => {
    const seen = new Map();
    for (const p of passes) {
      if (!seen.has(p.session_id)) {
        // session_label distinguishes same-day sessions (session_name is just
        // the track); date first so the newest-first ordering reads naturally
        const name = p.session_label || p.session_name || p.session_id.slice(0, 8);
        seen.set(p.session_id, {
          id: p.session_id,
          label: `${p.event_date || '?'} — ${name}`,
        });
      }
    }
    return [...seen.values()];
  }, [passes]);

  const laps = useMemo(() => {
    const byLap = new Map();
    for (const p of passes) {
      if (p.session_id === sessionId && !byLap.has(p.lap_number)) {
        byLap.set(p.lap_number, p.lap_time_seconds);
      }
    }
    return [...byLap.entries()]
      .map(([lap, time]) => ({ lap, time }))
      .sort((a, b) => a.lap - b.lap);
  }, [passes, sessionId]);

  const bestLap = useMemo(() => {
    const timed = laps.filter(l => l.time != null);
    return timed.length
      ? timed.reduce((a, b) => (b.time < a.time ? b : a)).lap
      : null;
  }, [laps]);

  const corners = useMemo(() =>
    passes.filter(p => p.session_id === sessionId && p.lap_number === lapNumber),
    [passes, sessionId, lapNumber]);

  // Default to the session's fastest lap — the pass you usually want to study.
  useEffect(() => { setLapNumber(bestLap ?? laps[0]?.lap ?? null); }, [laps, bestLap]);
  useEffect(() => { setCornerId(corners[0]?.corner_id ?? null); }, [corners]);

  useEffect(() => {
    if (!trackId || !cornerId) { setArchetypes([]); return; }
    let cancelled = false;
    getCornerArchetypes(trackId, cornerId)
      .then(a => !cancelled && setArchetypes(a))
      .catch(() => !cancelled && setArchetypes([]));
    return () => { cancelled = true; };
  }, [trackId, cornerId]);

  const search = () => {
    if (!sessionId || lapNumber == null || !cornerId) return;
    setLoading(true);
    setError(null);
    getSimilarityNeighbors({
      sessionId, lapNumber, cornerId, scope, otherDrivers, weights,
    }).then(r => {
      setResult(r);
      setChecked(r.neighbors.slice(0, 2).map(nkey));   // top-2 overlaid by default
      setLoading(false);
    }).catch(e => { setError(String(e.message || e)); setLoading(false); });
  };

  const toggleChecked = (k) => {
    setChecked(prev => prev.includes(k)
      ? prev.filter(x => x !== k)
      : (prev.length < 3 ? [...prev, k] : prev));
  };

  const overlaid = (result?.neighbors || []).filter(n => checked.includes(nkey(n)));
  const shapeRows = useMemo(() => {
    if (!result?.query?.speed_shape) return { speed: [], curv: [], names: [] };
    const q = result.query;
    const names = ['Query', ...overlaid.map(n => `${n.driver} L${n.lap_number}`)];
    const mk = (field) => q[field].map((v, i) => {
      const row = { pct: Math.round((i / 31) * 100), Query: v };
      overlaid.forEach((n, j) => { row[names[j + 1]] = n[field]?.[i] ?? null; });
      return row;
    });
    return { speed: mk('speed_shape'), curv: mk('curv_shape'), names };
  }, [result, checked]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <span>Corner neighbor explorer</span>
        <span className={styles.hint}>nearest passes to one corner pass — the raw vector search</span>
      </div>

      <div className={styles.explorerControls}>
        <label className={styles.ctl}>
          <span>Session</span>
          <select value={sessionId || ''} onChange={e => setSessionId(e.target.value || null)}>
            <option value="">— pick —</option>
            {sessions.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
        <label className={styles.ctl} title="Defaults to the session's fastest lap.">
          <span>Lap</span>
          <select value={lapNumber ?? ''} onChange={e => setLapNumber(Number(e.target.value))}>
            {laps.map(l => (
              <option key={l.lap} value={l.lap}>
                {l.lap} — {fmtLap(l.time)}{l.lap === bestLap ? ' ★' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.ctl}>
          <span>Corner</span>
          <select value={cornerId || ''} onChange={e => setCornerId(e.target.value)}>
            {corners.map(c => (
              <option key={c.corner_id} value={c.corner_id}>
                {c.corner_id} (vmin {c.vmin_ms?.toFixed(1)} m/s)
              </option>
            ))}
          </select>
        </label>
        <label className={styles.ctl}
               title={'Where to search for neighbors. Same corner = other passes '
                    + 'through this exact turn (apples to apples). Same track = any '
                    + 'corner here. All tracks = every corner in the database — '
                    + 'finds corners anywhere that are driven like this one.'}>
          <span>Scope</span>
          <select value={scope} onChange={e => setScope(e.target.value)}>
            <option value="corner">Same corner</option>
            <option value="track">Same track</option>
            <option value="all">All tracks (archetype hunt)</option>
          </select>
        </label>
        <label className={styles.check}
               title={'Exclude the viewed driver\'s own passes — otherwise their '
                    + 'other laps usually dominate the top of the list.'}>
          <input type="checkbox" checked={otherDrivers}
                 onChange={e => setOtherDrivers(e.target.checked)} />
          other drivers only
        </label>
        <button className={styles.searchBtn} onClick={search}
                disabled={!cornerId || loading}>
          <Search size={13} /> {loading ? 'Searching…' : 'Search'}
        </button>
      </div>

      <WeightSliders value={weights} onChange={onWeights} />
      {error && <div className={styles.error}>{error}</div>}

      {result && (
        <>
          <div className={styles.hint} style={{ margin: '8px 0' }}>
            Query: <strong>{result.query.driver}</strong>
            {result.query.vehicle_name ? ` · ${result.query.vehicle_name}` : ''} @ {result.query.track_id}{' '}
            {result.query.corner_id}, lap {result.query.lap_number}{' '}
            ({fmtLap(result.query.lap_time_seconds)}, vmin {result.query.vmin_ms?.toFixed(1)} m/s)
          </div>

          <div className={styles.shapeRow}>
            <ShapeChart title="Speed shape (fraction of approach speed)"
                        dataKeyRows={shapeRows.speed} seriesNames={shapeRows.names}
                        yDomain={[0, 1.3]} yLabel="v / v-approach" />
            <ShapeChart title="Line shape (curvature, fraction of own peak)"
                        dataKeyRows={shapeRows.curv} seriesNames={shapeRows.names}
                        yDomain={[-1.1, 1.1]} yLabel="κ / κ-peak" />
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th title="Overlay on the shape charts (max 3)">Plot</th>
                  <th>Driver</th><th>Car</th><th>Track</th><th>Corner</th><th>Lap</th>
                  <th>Lap time</th>
                  <th title="Minimum speed in the corner (m/s).">vMin</th>
                  <th title={'The ranking key: each block distance to the right, '
                           + 'multiplied by its slider weight, then summed. Change '
                           + 'the weights and re-Search to re-rank.'}>Total</th>
                  <th title={'Distance between the two 32-point speed profiles '
                           + '(each a fraction of its own approach speed). '
                           + 'Unweighted — sliders only affect Total.'}>Speed</th>
                  <th title={'Distance between the 32-point lateral-G profiles '
                           + '(normalized by each session\'s grip ceiling, '
                           + 'left/right corners sign-aligned). Unweighted.'}>LatG</th>
                  <th title={'Distance between the 32-point braking/drive profiles '
                           + '(GPS-derived longitudinal G, braking negative). '
                           + 'Unweighted.'}>LonG</th>
                  <th title={'Distance between the 32-point path-curvature shapes — '
                           + 'the driven line itself, independent of speed and car. '
                           + 'Unweighted.'}>Line</th>
                  <th title={'Distance between the 16 technique-habit ratios '
                           + '(trail fraction, coast fraction, release character, '
                           + 'apex timing, track-width margins, …). Unweighted.'}>Tech</th>
                </tr>
              </thead>
              <tbody>
                {result.neighbors.map(n => {
                  const k = nkey(n);
                  const ovIdx = checked.indexOf(k);
                  return (
                    <tr key={k}>
                      <td>
                        <input type="checkbox" checked={ovIdx >= 0}
                               disabled={ovIdx < 0 && checked.length >= 3}
                               onChange={() => toggleChecked(k)} />
                        {ovIdx >= 0 && (
                          <i className={styles.ovSwatch}
                             style={{ background: SERIES_COLORS[ovIdx + 1] }} />
                        )}
                      </td>
                      <td>{n.driver || '?'}</td>
                      <td>{n.vehicle_name || '—'}</td>
                      <td>{n.track_id || '?'}</td>
                      <td>{n.corner_id}</td>
                      <td>{n.lap_number}</td>
                      <td>{fmtLap(n.lap_time_seconds)}</td>
                      <td>{n.vmin_ms?.toFixed(1)}</td>
                      <td><strong>{n.dist_total.toFixed(3)}</strong></td>
                      <td>{n.dists.speed.toFixed(2)}</td>
                      <td>{n.dists.latg.toFixed(2)}</td>
                      <td>{n.dists.long.toFixed(2)}</td>
                      <td>{n.dists.curv.toFixed(2)}</td>
                      <td>{n.dists.metrics.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {archetypes.length > 0 && (
        <details className={styles.archetypes}>
          <summary>Corners anywhere like {trackId} {cornerId} (descriptor match)</summary>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Track</th><th>Corner</th>
                <th title={'Clean passes that built this corner\'s median '
                         + 'fingerprint — more instances, steadier fingerprint.'}>Instances</th>
                <th title={'Distance between the two corners\' median curvature '
                         + 'shapes — pure driven-line geometry.'}>Line dist</th>
                <th title={'Distance between 8-number corner characters: length, '
                         + 'tightness (peak & mean curvature), speed shed, typical '
                         + 'vMin and approach speed, trail and coast fractions.'}>Character dist</th>
                <th title="Ranking key: line dist + 3 × character dist.">Blended</th>
              </tr>
            </thead>
            <tbody>
              {archetypes.map(a => (
                <tr key={`${a.track_id}-${a.corner_id}`}>
                  <td>{a.track_id}</td><td>{a.corner_id}</td><td>{a.n_instances}</td>
                  <td>{a.line_dist.toFixed(3)}</td><td>{a.char_dist.toFixed(3)}</td>
                  <td>{a.blended.toFixed(3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}
